/*
 *  Binary Tree Data Structure
 *  Implements a binary tree using the Binary Tree Node for nodes
 *
 *  Angular module: trackerbase-app.services.binary-tree
 *  Exposes: BinaryTree
 */

import angular from "angular";

import Assert from "tbAssert";
import BinaryTreeNode from "./BinaryTreeNode";

const mLogSvc = new WeakMap();

class BinaryTree {
    constructor($log, rootNodeData) {
        Assert.StrictAssert(($log != null), "Assertion Failed: Missing Log Service Dependency");
        mLogSvc.set(this, $log);

        this._rootNode = new BinaryTreeNode(rootNodeData);
    }

    _isInThisTree(targetNode) {
        let checkNode = targetNode;
        while ((checkNode != null) && (checkNode != this.root)) {
            checkNode = checkNode.parent;
        }

        //Don't want strict equality, just want to see if reference addresses match.
        return (checkNode == this.root);
    }

    _walkTreeImpl(currentNode, visitorFn) {

        if (currentNode.isLeaf()) {
            return visitorFn(currentNode.data, null);
        }

        let childWalkResults = [currentNode.leftChild, currentNode.rightChild]
            .filter(function(childNode) {
                return ((angular.isDefined(childNode)) && (childNode != null));
            })
            .map((childNode) => {
                return this._walkTreeImpl(childNode, visitorFn);
            });

        return visitorFn(currentNode.data, childWalkResults);
    }

    toString() {
        let treeNodesStr = this.root.toString();
        let treeStr = `BinaryTree: \n${treeNodesStr}\n`;
        return treeStr;
    }

    prettyPrint(lineSep) {
        let prettyBinaryTreeNodes = this.root.prettyPrint(1,lineSep);
        let prettyBinaryTreeStr = `BinaryTree:${lineSep}${prettyBinaryTreeNodes}${lineSep}`;
        return prettyBinaryTreeStr;
    }
    get root() {
        return this._rootNode;
    }

    isLeftChildOfParent(targetNode) {
        if (targetNode.isRoot()) { return null; }

        let nodeParent = targetNode.parent;
        return (nodeParent.leftChild == targetNode);
    }

    isRightChildOfParent(targetNode) {
        if (targetNode.isRoot()) { return null; }

        let nodeParent = targetNode.parent;
        return (nodeParent.rightChild == targetNode);
    }

    insertAsLeftChild(targetParent, nodeData) {
        let oldChild = targetParent.leftChild;

        let newChild = new BinaryTreeNode(nodeData);
        newChild.leftChild = oldChild;
        newChild.parent = targetParent;

        if (oldChild != null) {
            oldChild.parent = newChild;
        }

        targetParent.leftChild = newChild;

        return newChild;
    }

    moveToLeftChild(targetNode, newParentNodeData) {

        let newParentNode = new BinaryTreeNode(newParentNodeData);

        if (targetNode.isRoot()) {
            this.replaceRoot(newParentNode);
        }
        else {
            let oldParentNode = targetNode.parent;

            if (this.isLeftChildOfParent(targetNode)) {
                oldParentNode.leftChild = newParentNode;

            }
            else {
                oldParentNode.rightChild = newParentNode;
            }

            newParentNode.parent = oldParentNode;
        }

        newParentNode.leftChild = targetNode;
        targetNode.parent = newParentNode;

        return targetNode;
    }

    insertAsRightChild(targetParent, nodeData) {
        let oldChild = targetParent.rightChild;

        let newChild = new BinaryTreeNode(nodeData);
        newChild.leftChild = oldChild;
        newChild.parent = targetParent;

        if (oldChild != null) {
            oldChild.parent = newChild;
        }

        targetParent.rightChild = newChild;

        return newChild;
    }

    moveToRightChild(targetNode, newParentNodeData) {
        let newParentNode = new BinaryTreeNode(newParentNodeData);

        if (targetNode.isRoot()) {
            this.replaceRoot(newParentNode);
        }
        else {
            let oldParentNode = targetNode.parent;

            if (this.isLeftChildOfParent(targetNode)) {
                oldParentNode.leftChild = newParentNode;

            }
            else {
                oldParentNode.rightChild = newParentNode;
            }

            newParentNode.parent = oldParentNode;
        }

        newParentNode.rightChild = targetNode;
        targetNode.parent = newParentNode;

        return targetNode;
    }

    deleteLeftChild(targetNode) {
        targetNode.leftChild.parent = null;
        targetNode.leftChild = null;
    }

    deleteRightChild(targetNode) {
        targetNode.rightChild.parent = null;
        targetNode.rightChild = null;
    }

    replaceRoot(newRootNode) {
        if ((angular.isUndefined(newRootNode)) ||
            (newRootNode == null))
        {
            mLogSvc.get(this).warning("BinaryTree Replace Root: Null or Undefined New Root Replacement Provided. Replacing Root With New, Blank Node.");
            this._rootNode = new BinaryTreeNode(null);
            return this.root;
        }

        mLogSvc.get(this).debug("Replacing Root Node. Old Root Node had " + String(this.root.childCount) + " children.");

        newRootNode.parent = null;
        this._rootNode = newRootNode;
        return this.root;
    }

    spliceOutNode(nodeToRemove) {
        mLogSvc.get(this).debug("Entering Splice Out Node");
        mLogSvc.get(this).debug("BinaryTree:");
        mLogSvc.get(this).debug(this.toString());
        mLogSvc.get(this).debug("Node to Remove");
        mLogSvc.get(this).debug(nodeToRemove.toString());

        let targetNumChildren = nodeToRemove.childCount;
        if (targetNumChildren > 1) {
            mLogSvc.get(this).error("Ambiguous splice: Not splicing node with multiple children");
            throw new Error("Rejecting requested ambiguous splice");
        }

        let replacementNode = null;
        if (nodeToRemove.leftChild != null) {
            replacementNode = nodeToRemove.leftChild;
            nodeToRemove.leftChild = null;
        }
        else if (nodeToRemove.rightChild != null) {
            replacementNode = nodeToRemove.rightChild;
            nodeToRemove.rightChild = null;
        }

        if (nodeToRemove == this.root)
        {
            mLogSvc.get(this).debug("Translating Node Splice Out Into BinaryTree Root Replace");
            //If the replacement root node has no children, this will result in a warning.
            return this.replaceRoot(replacementNode);
        }

        if (nodeToRemove.parent == null) {
            mLogSvc.get(this).error("Not splicing parent-less node that is not this tree's root");
            throw new Error("Cannot splice parent-less node from a different (or no) tree");
        }

        let parentOfNodeToRemove = nodeToRemove.parent;
        let deleteTargetIsLeftChild = undefined;

        if (parentOfNodeToRemove.leftChild == nodeToRemove) {
            deleteTargetIsLeftChild = true;
        }
        else if(parentOfNodeToRemove.rightChild == nodeToRemove) {
            deleteTargetIsLeftChild = false;
        }
        else {
            mLogSvc.get(this).error("Programming Error: Parent did not seem to match child.");
            throw new Error("Aborting delete-as-splice: Invalid parent-child relationships");
        }

        if (targetNumChildren === 0) {
            mLogSvc.get(this).debug("Splice Out Node: No Children, So We're Just Removing Removal Target.");
            if (deleteTargetIsLeftChild === true) {
                this.deleteLeftChild(parentOfNodeToRemove);
            }
            else if (deleteTargetIsLeftChild === false) {
                this.deleteRightChild(parentOfNodeToRemove);
            }
            else {
                mLogSvc.get(this).error("Programming Error: Parent did not seem to match child.");
                throw new Error("Aborting delete-as-splice: Invalid parent-child relationships");
            }

            mLogSvc.get(this).debug("Exiting Splice Out Node (No Children)");
            mLogSvc.get(this).debug("BinaryTree:");
            mLogSvc.get(this).debug(this.toString());
            return parentOfNodeToRemove;
        }

        if (targetNumChildren !== 1) {
            mLogSvc.get(this).error("Target node has neither 0, 1 nor more than 2 children. Aborting.");
            throw new Error("Abort: Children Count Should Not Be Possible.");
        }

        if (replacementNode == null) {
            mLogSvc.get(this).error("Abort: Null Replacement Node, But The Node To Remove Has Children.");
            throw new Error("Abort: Null Replacement Node, But The Node To Remove Has Children.");
        }

        replacementNode.parent = parentOfNodeToRemove;
        if (deleteTargetIsLeftChild) {
            parentOfNodeToRemove.leftChild = replacementNode;
        }
        else {
            parentOfNodeToRemove.rightChild = replacementNode;
        }
        nodeToRemove.parent = null;

        mLogSvc.get(this).debug("Exiting Splice Out Node (1 Child)");
        mLogSvc.get(this).debug("BinaryTree:");
        mLogSvc.get(this).debug(this.toString());
        return replacementNode;

    }

    findParentWithValue(startNode, comparisonFn) {
        let currentNode = startNode;
        let searchResult = undefined;
        let stopTraverse = false;

        mLogSvc.get(this).debug("Starting Find Parent With Value");
        while (stopTraverse !== true)
        {
            mLogSvc.get(this).debug("Checking This Node");
            mLogSvc.get(this).debug(currentNode);

            if (currentNode == this.root) {
                stopTraverse = true;
                mLogSvc.get(this).debug("Will stop traverse - current node is root");
            }

            if (comparisonFn(currentNode.data) === true) {
                searchResult = currentNode;
                stopTraverse = true;
                mLogSvc.get(this).debug("Found value we were seeking.");
            }

            if (stopTraverse !== true) {
                currentNode = currentNode.parent;
            }
        }

        mLogSvc.get(this).debug("Exiting Find Parent With Value. Result Is");
        mLogSvc.get(this).debug(searchResult);
        return searchResult;
    }

    walkTree(visitorFn) {
        let treeWalkResult = this._walkTreeImpl(this.root, visitorFn);
        return treeWalkResult;
    }
}

export default BinaryTree;



//////////////////
// WEBPACK FOOTER
// ./src/services/binary-tree/BinaryTree.js
// module id = Q0Wr
// module chunks = 1